import React from "react";
import { Award, Linkedin } from "lucide-react";

const FOUNDERS = [
  {
    name: "Juliette O'Brien",
    honor: "OAM",
    role: "Data Journalist",
    bio: "Data journalist. Built Australia's first COVID-19 dashboard, with visualisations trusted by media nationwide. Walkley Award nominee for innovation. Forbes contributor.",
    img: "/brand/team/juliette.jpg",
    initials: "JO",
    linkedin: "https://www.linkedin.com/in/julietteobrien/",
    testid: "founder-juliette",
  },
  {
    name: "Anthony Macali",
    honor: "OAM",
    role: "Data Analyst & Engineer",
    bio: "Data analyst and engineer. Architected the dashboard platform and live data pipelines relied on by government and institutions including Google and Johns Hopkins.",
    img: "/brand/team/anthony.webp",
    initials: "AM",
    linkedin: "https://www.linkedin.com/in/anthony-macali/",
    testid: "founder-anthony",
  },
];

export default function Team() {
  return (
    <section id="team" data-testid="team-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <p className="overline mb-4">The team</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1]">
              Led by award-<br />winning data<br />specialists.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-lg text-[#4B5563] leading-relaxed">
              OnlyFacts is built by a team trusted by millions of readers and dozens of organisations to deliver fast-moving, evidence-based communications.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
          {FOUNDERS.map((f) => (
            <article
              key={f.name}
              data-testid={f.testid}
              className="group bg-white border border-black/10 hover:border-[#0A0A0A] transition-colors"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-[#F3F4F6]">
                <div className="absolute inset-0 grid-bg opacity-40" />
                <div
                  className="absolute inset-0 bg-center bg-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  style={{ backgroundImage: `url(${f.img})` }}
                />
                <div className="absolute top-4 left-4 bg-white px-3 py-1.5 border border-black/10">
                  <span className="overline">{f.role}</span>
                </div>
                <div className="absolute top-4 right-4 w-12 h-12 bg-[#0052FF] text-white flex items-center justify-center font-display font-black">
                  {f.initials}
                </div>
                <div className="absolute bottom-4 left-4 bg-[#FDE047] text-[#0A0A0A] px-3 py-1.5 inline-flex items-center gap-1.5 font-mono text-xs font-semibold">
                  <Award size={14} /> OAM recipient
                </div>
              </div>
              <div className="p-6 md:p-8">
                <h3 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-1">
                  {f.name} <span className="text-[#0052FF]">{f.honor}</span>
                </h3>
                <div className="flex items-center gap-3 mb-4">
                  <p className="text-sm font-mono text-[#6B7280] uppercase tracking-wider">Co-founder / {f.role}</p>
                  <a
                    href={f.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${f.name} on LinkedIn`}
                    data-testid={`${f.testid}-linkedin`}
                    className="inline-flex items-center justify-center w-7 h-7 border border-black/15 text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors"
                  >
                    <Linkedin size={14} strokeWidth={1.75} />
                  </a>
                </div>
                <p className="text-base text-[#4B5563] leading-relaxed">{f.bio}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
